import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/TestAuthContext';
import { FileText, Upload, Loader2, CheckCircle, AlertCircle, TrendingUp, Info, Users, Search } from 'lucide-react';
import { resolveGuaranteedPayments } from '../../utils/k1Fields';
import { FinancialValue } from '../shared/FinancialValue';

/**
 * TaxReturnUpload - Parse IRS Form 1065 and Reconcile with P&L
 * 
 * Phase 2 Feature: Tax Return Parser
 * - Upload 1065 PDF
 * - AI extracts data from all schedules and supporting docs
 * - Reconciles with P&L
 * - Suggests adjustments
 * - Calculates claim impact
 */

interface TaxReturnUploadProps {
  onDataParsed?: (taxData: any) => void;
  plData?: any; // P&L data for reconciliation
  demoMode?: boolean; // Use demo endpoint without auth
  existingFiles?: Array<{
    filename: string;
    year: number;
    data: any;
    uploadedAt?: string;
  }>; // Previously uploaded files to display
}

export default function TaxReturnUpload({ onDataParsed, plData, demoMode = false, existingFiles = [] }: TaxReturnUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [taxData, setTaxData] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // NEW: Track multiple 1065 uploads by year - Initialize with existing files
  const [tax1065Files, setTax1065Files] = useState<Array<{
    filename: string;
    year: number | null;
    data: any;
    uploadedAt?: string;
  }>>(existingFiles);

  // Notify parent whenever tax1065Files changes
  React.useEffect(() => {
    if (onDataParsed && tax1065Files.length > 0) {
      onDataParsed(tax1065Files);
    }
  }, [tax1065Files, onDataParsed]);

  const { getToken } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // NEW: Detect year from 1065 filename or parsed data
  const detectTaxYear = (filename: string, parsedData: any = null) => {
    // Try filename first
    const yearMatch = filename.match(/20\d{2}/);
    if (yearMatch) {
      return parseInt(yearMatch[0]);
    }
    
    // Try parsed tax_year field
    if (parsedData?.tax_year) {
      return parseInt(parsedData.tax_year);
    }
    
    // Cannot detect a tax year.  Return null so the caller can surface
    // "year unknown" rather than silently tagging this 1065 as current year
    // (which was the source of phantom 2026-04 rows on Summary pages).
    return null;
  };

  // Upload and parse 1065 - USING XMLHttpRequest to bypass rrweb-recorder
  // NOW SUPPORTS MULTIPLE FILES (4 years of 1065s + 8 K-1s)
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    console.log(`📤 Uploading ${acceptedFiles.length} file(s)...`);
    setUploading(true);
    
    // Track completed uploads
    let completedUploads = 0;
    const totalUploads = acceptedFiles.length;

    // Process ALL accepted files sequentially
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      console.log(`   Processing file ${i + 1}/${totalUploads}: ${file.name}`);
    
    // Support multiple file types (v1.4 Enhancement)
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/x-csv',
      'text/plain' // For .IIF files
    ];
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'xlsx', 'xls', 'csv', 'iif', 'qbo'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension || '')) {
      setError('Please upload a PDF, Excel (.xlsx, .xls), CSV, or QuickBooks (.iif, .qbo) file');
      setUploading(false);
      return;
    }

    // Don't set uploading here - it's already set before the loop
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    // Use demo endpoint if in demo mode
    const endpoint = demoMode 
      ? `${BACKEND_URL}/api/v1/tax/parse-1065-demo`
      : `${BACKEND_URL}/api/v1/tax/parse-1065`;

    // Get auth token for non-demo mode
    let authToken = null;
    if (!demoMode) {
      try {
        authToken = await getToken();
      } catch (err) {
        console.error('Failed to get auth token:', err);
        setError('Authentication failed. Please sign in again.');
        setUploading(false);
        return;
      }
    }

    // BYPASS rrweb-recorder: Use XMLHttpRequest instead of fetch
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, true);
    
    // Add auth header for non-demo mode
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }

    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // SUCCESS
          const detectedYear = detectTaxYear(file.name, result.data);
          
          // Add to 1065 files list with year
          const tax1065Entry = {
            filename: file.name,
            year: detectedYear,
            data: result.data,
            uploadedAt: new Date().toISOString()
          };
          
          setTax1065Files(prev => [...prev, tax1065Entry]);
          
          // Also update single tax data for backward compatibility
          setTaxData(result.data);
          setUploadedFile({
            name: file.name,
            size: file.size
          });

          // DEBUG: Log form_type to verify K-1 detection
          console.log(`✅ Tax file uploaded: ${file.name} → Year ${detectedYear}`);
          console.log(`   Form Type: "${result.data.form_type}" (Type: ${typeof result.data.form_type})`);
          console.log(`   Partner Name: ${result.data.partner_name || 'N/A'}`);
          console.log(`   Business Name: ${result.data.business_name || 'N/A'}`);

          // Auto-reconcile if P&L data exists (use last uploaded file)
          if (plData && acceptedFiles[acceptedFiles.length - 1] === file) {
            reconcileData(result.data, plData);
          }
        } else {
          // ERROR
          setError(result.detail || result.message || 'Upload failed');
        }
      } catch (parseError) {
        setError('Invalid response from server');
      }
      
      // Track completed uploads
      completedUploads++;
      console.log(`   Upload ${completedUploads}/${totalUploads} completed`);
      
      // All uploads complete - re-enable upload
      if (completedUploads === totalUploads) {
        setUploading(false);
        console.log(`✅ All ${totalUploads} file(s) uploaded successfully`);
        if (totalUploads > 1) {
          setUploadMessage(`Processed ${totalUploads} files! Check the list below for all uploaded forms.`);
        }
      }
    };

    xhr.onerror = () => {
      setError(`Network error uploading ${file.name}`);
      
      // Track completed uploads (even on error)
      completedUploads++;
      console.log(`   Upload ${completedUploads}/${totalUploads} completed (error)`);
      
      // All uploads complete - re-enable upload
      if (completedUploads === totalUploads) {
        setUploading(false);
      }
    };

    xhr.ontimeout = () => {
      setError(`Upload timed out for ${file.name}`);
      
      // Track completed uploads (even on timeout)
      completedUploads++;
      console.log(`   Upload ${completedUploads}/${totalUploads} completed (timeout)`);
      
      // All uploads complete - re-enable upload
      if (completedUploads === totalUploads) {
        setUploading(false);
      }
    };

    // Set timeout to 60 seconds (Claude can be slow)
    xhr.timeout = 60000;

    xhr.send(formData);
    }  // End of for loop for multiple files
    
    // Note: setUploading(false) is now handled in xhr response handlers
    // after ALL uploads complete, not immediately after loop ends
  }, [BACKEND_URL, plData, onDataParsed, demoMode, getToken, detectTaxYear]);

  // Reconcile tax data with P&L - USING XMLHttpRequest to bypass rrweb-recorder
  const reconcileData = async (tax: any, pl: any) => {
    setReconciling(true);
    setError(null);

    // Get auth token if not in demo mode
    let authToken = null;
    if (!demoMode) {
      try {
        authToken = await getToken();
      } catch (err) {
        console.error('Failed to get auth token for reconciliation:', err);
        setError('Authentication failed. Please sign in again.');
        setReconciling(false);
        return;
      }
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/v1/tax/reconcile`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    // Add auth header if available
    if (authToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
    }

    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          // SUCCESS
          setReconciliation(result.reconciliation);
        } else {
          // ERROR
          setError(result.detail || result.message || 'Reconciliation failed');
        }
      } catch (parseError) {
        setError('Invalid response from server');
      } finally {
        setReconciling(false);
      }
    };

    xhr.onerror = () => {
      setError('Network error during reconciliation');
      setReconciling(false);
    };

    xhr.send(JSON.stringify({
      claim_id: `claim-${Date.now()}`,
      tax_data: tax,
      pl_data: pl
    }));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/x-csv': ['.csv'],
      'text/plain': ['.iif'], // QuickBooks IIF
      'application/vnd.intu.qbo': ['.qbo'] // QuickBooks Online
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true  // ✅ ENABLE MULTIPLE 1065 FILES (4 years of tax returns)
  });

  // Approve adjustments
  const handleApprove = () => {
    // TODO: Apply adjustments to claim
    setUploadMessage('Adjustments approved! Claim updated.');
  };

  return (
    <div className="rounded-lg shadow-lg p-6 mb-6 border-l-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderLeftColor: 'var(--color-brand)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
            Upload Form 1065 (Optional - Tax Return)
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Upload complete Form 1065 with all schedules (K, L, M, etc.) and supporting financial documents
          </p>
        </div>
        <div className="px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
          <span className="text-xs font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Phase 2 Feature</span>
        </div>
      </div>

      {/* Upload Area - ALWAYS SHOW (not conditioned on taxData) */}
      <div
        {...getRootProps()}
        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
        style={
          isDragActive
            ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }
            : { borderColor: 'var(--color-border)' }
        }
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-10 h-10 animate-spin mb-3" style={{ color: 'var(--color-brand)' }} />
            <p className="font-semibold">Parsing Form 1065...</p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Extracting Schedule K data</p>
          </div>
        ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-10 h-10 mb-2" style={{ color: 'var(--color-text-dim)' }} />
              <p className="font-semibold mb-1">
                {isDragActive ? 'Drop files here...' : 'Upload Financial Documents'}
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                PDF, Excel (.xlsx, .xls), CSV, QuickBooks (.iif, .qbo)
              </p>
            </div>
          )}
      </div>

      {/* K-1 Info Banner */}
      <div className="mt-4 border rounded-lg p-4 flex items-start gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
        <div className="text-sm">
          <p className="font-semibold mb-1" style={{ color: 'var(--color-needs-human-text)' }}>K-1 Schedules (Officer Compensation)</p>
          <p style={{ color: 'var(--color-needs-human-text)' }}>
            For accurate BI calculations, include Schedule K-1 forms showing Box 1 (partner income).
            If K-1s are not included, the system will use payroll data as a fallback for officer compensation.
          </p>
          <ul className="mt-2 space-y-1 ml-4 list-disc" style={{ color: 'var(--color-needs-human-text)' }}>
            <li>Upload complete 1065 PDFs including K-1 schedules for best results</li>
            <li>Or upload K-1 schedules separately as additional files</li>
            <li>System auto-detects and uses payroll when K-1s are missing</li>
          </ul>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-4 border rounded-lg p-4 flex items-start gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
          <div>
            <p className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Error</p>
            <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
          </div>
        </div>
      )}

      {/* NEW: Display uploaded 1065 files by year */}
      {tax1065Files.length > 0 && (
        <div className="mt-6 border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Uploaded 1065 Forms ({tax1065Files.length})</h3>
          <div className="grid grid-cols-1 gap-3">
            {tax1065Files
              .sort((a, b) => a.year - b.year)  // Sort by year
              .map((taxFile, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{taxFile.filename}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        Tax Year: <span className="font-semibold">{taxFile.year}</span>
                        {taxFile.data?.business_name && ` • ${taxFile.data.business_name}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setTax1065Files(prev => prev.filter((_, i) => i !== index))}
                    className="text-sm font-medium px-3 py-1 rounded"
                    style={{ color: 'var(--color-needs-human-text)' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
          </div>

          {/* Year coverage indicator */}
          <div className="mt-4 p-3 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
            <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
              <strong>Tax years covered:</strong> {[...new Set(tax1065Files.map(f => f.year))].sort().join(', ')}
            </p>
            {tax1065Files.length < 4 && (
              <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'var(--color-brand)' }}>
                <AlertCircle className="w-3.5 h-3.5" /> Note: BI analysis requires 4 years of data (current year + 3 prior years)
              </p>
            )}
          </div>
          
          {/* 1065 FORM MATRIX - Multi-year view of all 1065 uploads */}
          {(() => {
            const form1065Files = tax1065Files.filter(f => f.data?.form_type === '1065');
            if (form1065Files.length === 0) return null;
            
            // Sort by year
            const sortedForms = form1065Files.sort((a, b) => a.year - b.year);
            const years = sortedForms.map(f => f.year);
            
            return (
              <div className="mt-6 border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <FileText className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  Form 1065 Summary - Multi-Year View ({form1065Files.length} years)
                </h3>

                <div className="border rounded-lg overflow-hidden mb-4" style={{ borderColor: 'var(--color-border)' }}>
                  <table className="w-full">
                    <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Schedule K Line Item</th>
                        {years.map(year => (
                          <th key={year} className="text-right px-4 py-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                            {year}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                      {/* Line 1: Ordinary Income */}
                      <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Line 1: Ordinary Income</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Business income (loss)</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                            <FinancialValue value={form.data?.schedule_k?.line_1_ordinary_income} />
                          </td>
                        ))}
                      </tr>

                      {/* Line 9: Salaries & Wages */}
                      <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Line 9: Salaries & Wages</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Total payroll baseline</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>
                            <FinancialValue value={form.data?.schedule_k?.line_9_salaries_wages} />
                          </td>
                        ))}
                      </tr>

                      {/* Line 13d: Other Deductions */}
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Line 13d: Other Deductions</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Miscellaneous expenses</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                            <FinancialValue value={form.data?.schedule_k?.line_13d_other_deductions} />
                          </td>
                        ))}
                      </tr>

                      {/* Line 22: Guaranteed Payments */}
                      <tr style={{ backgroundColor: 'transparent' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Guaranteed Payments (Line 4 / Page 1 Line 10)</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Officer compensation (B2T). Label was "Line 22" historically but Line 22 is ordinary income; guaranteed payments live on Schedule K Line 4 / Page 1 Line 10.</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold" style={{ color: 'var(--color-verified)' }}>
                            <FinancialValue value={resolveGuaranteedPayments(form.data)} />
                          </td>
                        ))}
                      </tr>

                      {/* Gross Receipts */}
                      <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Gross Receipts (Line 1c)</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Total revenue</p>
                        </td>
                        {sortedForms.map(form => (
                          <td key={form.year} className="text-right px-4 py-3 font-mono font-semibold" style={{ color: 'var(--color-brand)' }}>
                            <FinancialValue value={form.data?.gross_receipts} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Business Info */}
                {sortedForms[0]?.data?.business_name && (
                  <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      <span className="font-semibold">Business:</span> {sortedForms[0].data.business_name}
                      {sortedForms[0].data.ein && ` • EIN: ${sortedForms[0].data.ein}`}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
          
          {/* K-1 PARTNER MATRIX - Aggregated view from 1065s and K-1s */}
          {(() => {
            // Aggregate K-1 data from both standalone K-1 files AND from 1065 forms (which include K-1 data)
            const filesWithK1Data = tax1065Files.filter(f => {
              // Include standalone K-1 files
              if (f.data?.form_type === 'K-1') return true;
              // Include 1065 files that have K-1 partner data
              if (f.data?.form_type === '1065' && f.data?.schedule_k1?.partners) return true;
              return false;
            });
            if (filesWithK1Data.length === 0) return null;
            
            const partnerData: Record<string, Record<string, number>> = {};
            const yearTotals: Record<string, number> = {};
            
            filesWithK1Data.forEach(file => {
              const data = file.data;
              const year = String(data.tax_year || file.year);
              
              // Check if this is a 1065 with embedded K-1 partners data
              if (data.form_type === '1065' && data.schedule_k1?.partners && Array.isArray(data.schedule_k1.partners)) {
                // Extract each partner from the 1065's K-1 data
                data.schedule_k1.partners.forEach((partner: any) => {
                  const partnerName = partner.name || 'Unknown Partner';
                  const box1Amount = partner.box_1_ordinary_income || 0;
                  
                  if (!partnerData[partnerName]) partnerData[partnerName] = {};
                  partnerData[partnerName][year] = (partnerData[partnerName][year] || 0) + box1Amount;
                  yearTotals[year] = (yearTotals[year] || 0) + box1Amount;
                });
              } else {
                // Standalone K-1 file
                const partnerName = data.partner_name || 'Unknown Partner';
                const box1Amount = data.schedule_k1?.total_officer_compensation || 0;
                
                if (!partnerData[partnerName]) partnerData[partnerName] = {};
                partnerData[partnerName][year] = box1Amount;
                yearTotals[year] = (yearTotals[year] || 0) + box1Amount;
              }
            });
            
            const partners = Object.keys(partnerData);
            const years = Object.keys(yearTotals).sort();
            const grandTotal = Object.values(yearTotals).reduce((sum, val) => sum + val, 0);
            
            return (
              <div className="mt-6 border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <Users className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                  K-1 Partner Summary ({partners.length} partner{partners.length !== 1 ? 's' : ''} • {years.length} year{years.length !== 1 ? 's' : ''})
                </h3>

                <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                  <table className="w-full">
                    <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Partner</th>
                        {years.map(year => (
                          <th key={year} className="text-right px-4 py-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                            {year}
                          </th>
                        ))}
                        <th className="text-right px-4 py-3 font-semibold" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                      {partners.map(partner => {
                        const partnerTotal = Object.values(partnerData[partner]).reduce((sum, val) => sum + val, 0);
                        return (
                          <tr key={partner}>
                            <td className="px-4 py-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>{partner}</td>
                            {years.map(year => (
                              <td key={year} className="text-right px-4 py-3" style={{ color: 'var(--color-text-secondary)' }}>
                                <FinancialValue value={partnerData[partner][year]} />
                              </td>
                            ))}
                            <td className="text-right px-4 py-3 font-semibold" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                              <FinancialValue value={partnerTotal} />
                            </td>
                          </tr>
                        );
                      })}

                      <tr className="font-bold" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <td className="px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>Total</td>
                        {years.map(year => (
                          <td key={year} className="text-right px-4 py-3" style={{ color: 'var(--color-needs-human-text)' }}>
                            <FinancialValue value={yearTotals[year]} />
                          </td>
                        ))}
                        <td className="text-right px-4 py-3 text-lg" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                          <FinancialValue value={grandTotal} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Parsed Tax Data */}
      {taxData && (
        <div className="mt-4 space-y-4">
          {/* DEBUG: Form Type Indicator */}
          <div className="border-2 p-3 rounded-lg" style={String(taxData.form_type).trim() === 'K-1' ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' } : { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' }}>
            <p className="font-bold text-lg flex items-center gap-2">
              <Search className="w-4 h-4" /> DEBUG: Detected Form Type = "{taxData.form_type}"
            </p>
            <p className="text-sm">
              Trimmed: "{String(taxData.form_type).trim()}" |
              Matches K-1: {String(taxData.form_type).trim() === 'K-1' ? 'YES' : 'NO'}
            </p>
          </div>

          {/* File Info */}
          <div className="border rounded-lg p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <div className="flex-1">
                <p className="font-semibold" style={{ color: 'var(--color-verified)' }}>{uploadedFile?.name}</p>
                <p className="text-xs" style={{ color: 'var(--color-verified)' }}>
                  Successfully parsed {taxData.form_type === 'K-1' ? 'Schedule K-1' : 'Form 1065'}
                  <span className="ml-2" style={{ color: 'var(--color-text-muted)' }}>(Type: {taxData.form_type})</span>
                </p>
              </div>
              {taxData.is_amended && (
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}>
                  Amended Return
                </span>
              )}
            </div>
          </div>

          {/* Business Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Business Name</p>
              <p className="font-semibold">{taxData.business_name}</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Tax Year</p>
              <p className="font-semibold">{taxData.tax_year}</p>
            </div>
          </div>

          {/* K-1 or 1065 Data Display */}
          <div>
            {(() => {
              // DEBUG: Log form_type during rendering
              const formType = taxData.form_type;
              const formTypeStr = String(formType).trim();
              const isK1 = formTypeStr === 'K-1';
              console.log(`🔍 Rendering tax display:`);
              console.log(`   form_type value: "${formType}"`);
              console.log(`   form_type type: ${typeof formType}`);
              console.log(`   form_type trimmed: "${formTypeStr}"`);
              console.log(`   Is K-1 (===): ${formTypeStr === 'K-1'}`);
              console.log(`   Is K-1 (includes): ${formTypeStr.includes('K-1')}`);
              return null;
            })()}
            {String(taxData.form_type).trim() === 'K-1' ? (
              /* K-1 Schedule Display */
              <>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Schedule K-1 - Partner: {taxData.partner_name || 'Unknown'}
                </h3>
                <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                  <p className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--color-needs-human-text)' }}><CheckCircle className="w-4 h-4" /> K-1 Detected - Partner Income Extracted</p>
                  <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                    This data will be aggregated with other K-1s in Step 5 → K-1 Partner Summary tab
                  </p>
                </div>
                <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                  <table className="w-full">
                    <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-semibold">Box</th>
                        <th className="text-right px-4 py-3 text-sm font-semibold">Amount</th>
                        <th className="text-center px-4 py-3 text-sm font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                      {(() => {
                        // K-1 box rows.  `taxData.k1_data` is the canonical
                        // source (post 2026-04-18 expansion).  Fall back to
                        // `schedule_k1.total_officer_compensation` for Box 1
                        // on legacy records that pre-date the expansion.
                        const k1: Record<string, number | null | undefined> = taxData?.k1_data || {};
                        const legacyBox1 =
                          k1.box_1_ordinary_income ??
                          taxData?.schedule_k1?.total_officer_compensation ??
                          null;

                        const K1_ROWS: Array<{
                          label: string;
                          sub: string;
                          value: number | null | undefined;
                        }> = [
                          { label: 'Box 1: Ordinary Business Income',     sub: "Partner's share of ordinary income", value: legacyBox1 },
                          { label: 'Box 2: Net Rental Real Estate Income', sub: 'Partner\u2019s share of rental RE income',        value: k1.box_2_net_rental_real_estate },
                          { label: 'Box 3: Other Rental Income',           sub: 'Partner\u2019s share of other rentals',           value: k1.box_3_other_rental_income },
                          { label: 'Box 4: Guaranteed Payments',           sub: 'Salary-like payments to partner',                 value: k1.box_4_guaranteed_payments },
                          { label: 'Box 5: Interest Income',               sub: 'Partner\u2019s share of interest',                value: k1.box_5_interest },
                          { label: 'Box 6: Ordinary Dividends',            sub: 'Partner\u2019s share of dividends',               value: k1.box_6_dividends },
                          { label: 'Box 7: Royalties',                     sub: 'Partner\u2019s share of royalty income',          value: k1.box_7_royalties },
                          { label: 'Box 8: Net Short-Term Capital Gain',   sub: 'ST capital gain (loss) allocation',               value: k1.box_8_net_st_capital_gain },
                          { label: 'Box 9a: Net Long-Term Capital Gain',   sub: 'LT capital gain (loss) allocation',               value: k1.box_9a_net_lt_capital_gain },
                          { label: 'Box 11: Other Income',                 sub: 'Partner\u2019s other income items',               value: k1.box_11_other_income },
                          { label: 'Box 14: Self-Employment Earnings',     sub: 'Earnings subject to SE tax',                      value: k1.box_14_self_employment_earnings },
                          { label: 'Box 19: Distributions',                sub: 'Cash / property distributed to partner',          value: k1.box_19_distributions },
                        ];

                        return K1_ROWS.map((row) => {
                          const extracted = row.value !== null && row.value !== undefined;
                          return (
                            <tr key={row.label}>
                              <td className="px-4 py-3">
                                <p className="font-medium">{row.label}</p>
                                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{row.sub}</p>
                              </td>
                              <td className={`text-right px-4 py-3 font-mono ${row.label.startsWith('Box 1:') ? 'font-semibold text-lg' : ''}`} style={{ color: Number(row.value) < 0 ? 'var(--color-text-muted)' : 'var(--color-text-primary)' }}>
                                <FinancialValue value={row.value} />
                              </td>
                              <td className="text-center px-4 py-3">
                                {extracted ? (
                                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>&#10003; Extracted</span>
                                ) : (
                                  <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>&mdash;</span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              /* Form 1065 Display */
              <>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Form 1065 - Partnership Return
                </h3>
                <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                  <p className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--color-needs-human-text)' }}><CheckCircle className="w-4 h-4" /> Form 1065 Detected - Key Data Extracted</p>
                  <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                    Critical fields for BI B2T calculations and officer compensation analysis
                  </p>
                </div>

                {/* Schedule K Critical Lines */}
                <div className="border rounded-lg overflow-hidden mb-4" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="px-4 py-2" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <p className="font-semibold text-sm">Schedule K - Partner's Distributive Share Items</p>
                  </div>
                  <table className="w-full">
                    <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-semibold">Line Item</th>
                        <th className="text-right px-4 py-3 text-sm font-semibold">Amount</th>
                        <th className="text-center px-4 py-3 text-sm font-semibold">BI Use</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 1: Ordinary Income</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Business income (loss)</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          <FinancialValue value={taxData.schedule_k?.line_1_ordinary_income} />
                        </td>
                        <td className="text-center px-4 py-3">
                          {taxData.has_negative_income ? (
                            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Loss</span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>Revenue Check</span>
                          )}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 9: Salaries & Wages</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Total payroll - Fixed baseline</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          <FinancialValue value={taxData.schedule_k?.line_9_salaries_wages ?? taxData.total_payroll} />
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Payroll Base</span>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">
                          <p className="font-medium">Line 13d: Other Deductions</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Miscellaneous expenses</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold">
                          <FinancialValue value={taxData.schedule_k?.line_13d_other_deductions} />
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Deduction</span>
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: 'transparent' }}>
                        <td className="px-4 py-3">
                          <p className="font-medium">Guaranteed Payments (Line 4 / Page 1 Line 10)</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Partner compensation — canonical source is Schedule K Line 4 or Page 1 Line 10. Prior label "Line 22" was wrong — Line 22 is ordinary income.</p>
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-semibold text-lg">
                          <FinancialValue value={resolveGuaranteedPayments(taxData) ?? taxData.schedule_k1?.total_officer_compensation} />
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>B2T &lt;5%</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Officer Compensation Summary */}
                {taxData.schedule_k1?.total_officer_compensation && (
                  <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                    <p className="font-semibold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Officer Compensation Summary</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p style={{ color: 'var(--color-needs-human-text)' }}>Total Officer Comp (K-1s)</p>
                        <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                          <FinancialValue value={taxData.schedule_k1.total_officer_compensation} />
                        </p>
                      </div>
                      {taxData.schedule_k1.partners && taxData.schedule_k1.partners.length > 0 && (
                        <div>
                          <p style={{ color: 'var(--color-needs-human-text)' }}>Partners</p>
                          <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                            {taxData.schedule_k1.partners.length}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Reconciliation Loading */}
          {reconciling && (
            <div className="border rounded-lg p-4 flex items-center gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-brand)' }} />
              <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>Reconciling with P&L data...</p>
            </div>
          )}

          {/* Reconciliation Results */}
          {reconciliation && (
            <div className="space-y-4">
              <div className="border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  Reconciliation Results
                </h3>

                {/* Claim Impact */}
                {reconciliation.claim_impact > 0 && (
                  <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Potential Claim Increase</p>
                        <p className="text-3xl font-bold flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                          <FinancialValue value={reconciliation.claim_impact} />
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Confidence</p>
                        <p className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
                          {((reconciliation.confidence || 0.95) * 100).toFixed(0)}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Discrepancies */}
                {reconciliation.discrepancies && reconciliation.discrepancies.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                      Identified Discrepancies
                    </h4>
                    <div className="space-y-2">
                      {reconciliation.discrepancies.map((disc, idx) => (
                        <div key={idx} className="border rounded p-3 text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium">{disc.item}</span>
                            <span className="font-mono font-semibold" style={{ color: 'var(--color-brand)' }}>
                              <FinancialValue value={Math.abs(disc.variance)} />
                            </span>
                          </div>
                          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{disc.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Adjustments */}
                {reconciliation.suggested_adjustments && reconciliation.suggested_adjustments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Suggested Adjustments</h4>
                    <div className="space-y-2 mb-4">
                      {reconciliation.suggested_adjustments.map((adj, idx) => (
                        <div key={idx} className="border rounded p-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-sm">{adj.category}</p>
                              <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{adj.reason}</p>
                            </div>
                            <span className="font-mono font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
                              +<FinancialValue value={adj.amount} />
                            </span>
                          </div>
                          {adj.tax_line && (
                            <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>From: {adj.tax_line}</p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Approve Button */}
                    <button
                      onClick={handleApprove}
                      className="w-full font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve Adjustments & Update Claim
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manual Reconcile Button */}
          {taxData && !reconciliation && !reconciling && plData && (
            <button
              onClick={() => reconcileData(taxData, plData)}
              className="w-full font-semibold py-3 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Reconcile with P&L Data
            </button>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-4 border rounded-lg p-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
          <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
            <strong>How it works:</strong> Upload your partnership/S-Corp tax return (Form 1065).
            AI extracts data from Form 1065 (all schedules: K, L, M, balance sheet, P&L), compares with your financial records, and suggests legitimate
            adjustments to maximize your claim.
          </p>
        </div>
      </div>
    </div>
  );
}
